import { FlatTable, FlatTableBody } from 'carbon-react/esm/components/flat-table';
import { FlatTableProps } from '../types/dsntable.types';
import { PAGE_SIZE_OPTIONS } from '../constants/dsntable.constants';
import PageNoResult from '../../../components/PageNoResult';
import TablePager from '../component/TablePager';
import { NotificationMessage } from '../../../components/NotificationMessage';
import { useDsnActions } from '../hooks/useDsnActions';
import { useTableRowHandling } from '../../../hooks/useTableRowHandling';
import { DsnTableHeader } from './DsnTableHeader';
import { DsnTableRow } from './DsnTableRow';
import { CellContextMenu } from './CellContextMenu';
import { useCellContextMenu, COPY_SUCCESS_FEEDBACK_DURATION_MS } from './useCellContextMenu';

const FlatTableComponent = ({
  data,
  totalCount = 0,
  onPaginationChange,
  currentPage,
  pageSize,
  initialActiveRowId,
  onSelectedRowsChange,
  isLoading = false,
  sort,
  onSort,
}: FlatTableProps) => {
  const rowHandling = useTableRowHandling({
    data,
    onPaginationChange,
    pageSize,
    initialActiveRowId,
    onSelectedRowsChange,
  });

  const { selectAll, handleSelectAllRows } = rowHandling;
  const { notification, ...actions } = useDsnActions();
  const contextMenu = useCellContextMenu();

  const tableProps = {
    totalRecords: totalCount,
    pageSize,
    currentPage,
    onPagination: rowHandling.handlePaginationChange,
    pageSizeSelectionOptions: PAGE_SIZE_OPTIONS,
  };

  if (!data?.length && !isLoading) {
    return <PageNoResult />;
  }

  return (
    <>
      {notification.showNotification && (
        <NotificationMessage
          open={notification.showNotification}
          onDismiss={notification.hideNotification}
          variant={notification.notificationVariant}
        >
          {notification.notificationMessage}
        </NotificationMessage>
      )}

      <div className="edi-list-table dsn-list-table" onContextMenu={contextMenu.handleContextMenu}>
        <FlatTable colorTheme="light" isZebra marginBottom={1}>
          <DsnTableHeader
            selectAll={selectAll}
            onSelectAllChange={handleSelectAllRows}
            sort={sort}
            onSort={onSort}
          />
          <FlatTableBody>
            {data.map((item, index) => (
              <DsnTableRow
                key={item.InstanceId ?? `row-${index}`}
                item={item}
                isHighlighted={rowHandling.highlightedRow === item.InstanceId}
                isActive={rowHandling.activeRow === item.InstanceId}
                currentPage={currentPage}
                pageSize={pageSize}
                {...rowHandling}
                {...actions}
              />
            ))}
          </FlatTableBody>
        </FlatTable>
      </div>

      <CellContextMenu
        visible={contextMenu.menuState.visible}
        x={contextMenu.menuState.x}
        y={contextMenu.menuState.y}
        onCopy={contextMenu.handleCopy}
        onClose={contextMenu.close}
      />

      {contextMenu.copySuccess && (
        <NotificationMessage
          open={contextMenu.copySuccess}
          onDismiss={contextMenu.dismissCopySuccess}
          variant="success"
          timeout={COPY_SUCCESS_FEEDBACK_DURATION_MS}
        >
          Contenu copié dans le presse-papiers
        </NotificationMessage>
      )}

      {contextMenu.copyError && (
        <NotificationMessage
          open={!!contextMenu.copyError}
          onDismiss={contextMenu.dismissCopyError}
          variant="error"
          timeout={5000}
        >
          {contextMenu.copyError}
        </NotificationMessage>
      )}

      <TablePager {...tableProps} />
    </>
  );
};

export default FlatTableComponent;
